import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Company } from '../types/auth';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, metadata?: { [key: string]: any }) => Promise<{ 
    data: { user: User | null; session: Session | null } | null; 
    error: any 
  }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Check for specific error messages
      if (error.message.includes('not confirmed')) {
        throw new Error('Email not confirmed: ' + error.message);
      }
      throw error;
    }

    setSession(data.session);
    setUser(data.user);
  };

  const signUp = async (email: string, password: string, metadata?: { [key: string]: any }) => {
    try {
      // Separate user-level data from company data
      const userData = metadata ? {
        voornaam: metadata.voornaam,
        achternaam: metadata.achternaam,
        telefoon: metadata.telefoon,
        acceptedTerms: metadata.acceptedTerms
      } : {};

      // Create user with Supabase auth
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signUpError) throw signUpError;
      if (!authData?.user) throw new Error('No user data returned');

      // Create company record if metadata is provided
      if (metadata) {
        const companyData = {
          user_id: authData.user.id,
          bedrijfsnaam: metadata.bedrijfsnaam,
          kvk_nummer: metadata.kvk_nummer,
          btw_nummer: metadata.btw_nummer,
          adres: metadata.adres,
          postcode: metadata.postcode,
          stad: metadata.stad,
          land: metadata.land,
          voornaam_financieel: metadata.voornaam_financieel,
          achternaam_financieel: metadata.achternaam_financieel,
          telefoon_financieel: metadata.telefoon_financieel,
          email_financieel: metadata.email_financieel
        };

        const { error: companyError } = await supabase
          .from('companies')
          .insert([companyData])
          .select()
          .single();

        if (companyError) {
          console.error('Error creating company:', companyError);
          throw new Error(`Failed to create company: ${companyError.message}`);
        }
      }

      // Return the user data without signing in automatically
      return { 
        data: { 
          user: authData.user,
          session: null 
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error in signUp:', error);
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Unknown error during signup')
      };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    setUser(null);
  };

  const value = {
    user,
    session,
    signIn,
    signUp,
    signOut,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
